import { useState, useEffect, useRef } from 'react';
import { useModalStore } from '../../../stores/modalStore.js';
import { useServerStore } from '../../../stores/serverStore.js';
import { useEventStore } from '../../../stores/eventStore.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

const TYPE_META = {
  voice:    { icon: '🔊', label: 'Voice Channel',  color: '#10b981' },
  stage:    { icon: '🎙️', label: 'Stage Channel',  color: '#6366f1' },
  external: { icon: '📍', label: 'External',        color: '#f59e0b' },
};

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(iso) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function timeUntil(iso) {
  const diff = new Date(iso) - Date.now();
  if (diff < 0) return 'Started';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h === 0) return `In ${m}m`;
  if (h < 24)  return `In ${h}h ${m}m`;
  return `In ${Math.floor(h/24)}d`;
}

function EventCard({ event, userId, onRSVP, onDelete, isAdmin }) {
  const meta = TYPE_META[event.type] || TYPE_META.external;
  const hasRSVP = (event.rsvps || []).includes(userId);
  const isUpcoming = new Date(event.startTime) > Date.now();

  return (
    <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, overflow: 'hidden', marginBottom: 10 }}>
      {event.coverImage && (
        <div style={{ height: 100, background: `url(${event.coverImage}) center/cover`, borderBottom: '1px solid rgba(255,255,255,0.07)' }} />
      )}
      {!event.coverImage && (
        <div style={{ height: 60, background: `linear-gradient(135deg, ${meta.color}33, ${meta.color}11)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>
          {meta.icon}
        </div>
      )}
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{event.title}</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}44`, padding: '1px 7px', borderRadius: 10, fontWeight: 700 }}>{meta.label}</span>
              {event.location && <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>📍 {event.location}</span>}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: isUpcoming ? '#34d399' : 'rgba(255,255,255,0.4)' }}>{timeUntil(event.startTime)}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>{formatDate(event.startTime)}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{formatTime(event.startTime)}</div>
          </div>
        </div>

        {event.description && (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5, marginBottom: 10 }}>{event.description}</div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
            {(event.rsvps || []).length} interested
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {isAdmin && (
              <button
                onClick={() => onDelete(event.id)}
                style={{ padding: '5px 10px', borderRadius: 6, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: 12, cursor: 'pointer' }}
              >
                Cancel
              </button>
            )}
            <button
              onClick={() => onRSVP(event.id, !hasRSVP)}
              style={{ padding: '5px 14px', borderRadius: 6, background: hasRSVP ? 'rgba(99,102,241,0.25)' : '#6366f1', border: `1px solid ${hasRSVP ? 'rgba(99,102,241,0.5)' : '#6366f1'}`, color: hasRSVP ? '#a5b4fc' : '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              {hasRSVP ? '✓ Interested' : '+ Interested'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const EMPTY_FORM = { title: '', description: '', type: 'voice', location: '', startTime: '', endTime: '' };

export function ServerEventsModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const currentServerId = useServerStore(s => s.currentServerId);
  const { getServerEvents, fetchEvents, createEvent, deleteEvent, rsvp } = useEventStore();

  const [view, setView] = useState('list');   // list | create
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const ref = useRef();
  useFocusTrap(ref, isOpen && type === 'serverEvents');

  const serverId = data?.serverId || currentServerId;
  const events = getServerEvents(serverId);
  const userId = localStorage.getItem('user_id') || 'me';

  useEffect(() => {
    if (isOpen && type === 'serverEvents') {
      fetchEvents(serverId);
      setView('list');
      setForm(EMPTY_FORM);
    }
  }, [isOpen, type, serverId]);

  if (!isOpen || type !== 'serverEvents') return null;

  const handleCreate = async () => {
    if (!form.title || !form.startTime) return;
    setSaving(true);
    await createEvent(serverId, form);
    setSaving(false);
    setView('list');
    setForm(EMPTY_FORM);
  };

  const upcoming = events.filter(e => new Date(e.startTime) > Date.now() && e.status !== 'cancelled');
  const past     = events.filter(e => new Date(e.startTime) <= Date.now() || e.status === 'cancelled');

  // Default datetime for form: 1 hour from now
  const defaultStart = new Date(Date.now() + 3600000).toISOString().slice(0, 16);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="Server Events"
        onClick={e => e.stopPropagation()}
        style={{ background: '#16213e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, width: 540, maxWidth: '95vw', maxHeight: '82vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>
            {view === 'create' ? '✨ Create Event' : '📅 Server Events'}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {view === 'list' && (
              <button
                onClick={() => setView('create')}
                style={{ padding: '6px 14px', borderRadius: 7, background: '#6366f1', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                + Create
              </button>
            )}
            <button onClick={view === 'create' ? () => setView('list') : onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* Create form */}
          {view === 'create' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { key: 'title', label: 'Event Name', placeholder: 'e.g. Game Night' },
                { key: 'location', label: 'Location / Channel', placeholder: 'e.g. Gaming Lounge' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>{label}</label>
                  <input
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
              ))}

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Event Type</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {Object.entries(TYPE_META).map(([k, m]) => (
                    <button
                      key={k}
                      onClick={() => setForm(f => ({ ...f, type: k }))}
                      style={{ flex: 1, padding: '8px', borderRadius: 7, border: `1px solid ${form.type === k ? m.color : 'rgba(255,255,255,0.1)'}`, background: form.type === k ? `${m.color}22` : 'rgba(255,255,255,0.04)', color: form.type === k ? m.color : 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {m.icon} {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {[{ key: 'startTime', label: 'Start Time' }, { key: 'endTime', label: 'End Time (optional)' }].map(({ key, label }) => (
                  <div key={key}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>{label}</label>
                    <input
                      type="datetime-local"
                      value={form[key]}
                      min={defaultStart}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box', colorScheme: 'dark' }}
                    />
                  </div>
                ))}
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What's happening?"
                  rows={3}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 14, outline: 'none', resize: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setView('list')} style={{ padding: '8px 18px', borderRadius: 7, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button
                  onClick={handleCreate}
                  disabled={!form.title || !form.startTime || saving}
                  style={{ padding: '8px 20px', borderRadius: 7, background: form.title && form.startTime ? '#6366f1' : 'rgba(99,102,241,0.4)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: form.title && form.startTime ? 'pointer' : 'default' }}
                >
                  {saving ? 'Creating…' : 'Create Event'}
                </button>
              </div>
            </div>
          )}

          {/* Events list */}
          {view === 'list' && (
            <>
              {upcoming.length === 0 && past.length === 0 && (
                <div style={{ textAlign: 'center', paddingTop: 50, color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>📅</div>
                  No events yet — create the first one!
                </div>
              )}
              {upcoming.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Upcoming — {upcoming.length}</div>
                  {upcoming.map(e => (
                    <EventCard key={e.id} event={e} userId={userId} onRSVP={(id, join) => rsvp(serverId, id, join)} onDelete={(id) => deleteEvent(serverId, id)} isAdmin={true} />
                  ))}
                </>
              )}
              {past.length > 0 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '16px 0 10px' }}>Past Events</div>
                  {past.map(e => (
                    <EventCard key={e.id} event={e} userId={userId} onRSVP={() => {}} onDelete={() => {}} isAdmin={false} />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
